import { useRoutes, useRouteMatcher } from '@folklore/routes';

import { useBrand, useDefaultBrand } from '../contexts/SiteContext';
import useCollection from './useCollection';
import useDocument from './useDocument';

export default function useBrandFromLocation(fullLocation: string | null): {
    brand: Brand | null;
    loaded: boolean;
} {
    const location = (fullLocation || '').split('?')[0];
    const routeMatcher = useRouteMatcher();
    const routes = useRoutes();

    const [brandMatch = false, { brand: brandParam = null } = {}] = routeMatcher(
        routes['brand.wildcard'],
        location,
    );

    const [documentMatch = false, { slug: documentParam = null } = {}] = routeMatcher(
        routes.document,
        location,
    );
    const { document = null, isFetched: documentIsFetched = false } = useDocument(documentParam, {
        suspense: false,
        enabled: documentParam !== null,
        // keepPreviousData: false,
        placeholderData: null,
    });

    const [collectionMatch = false, { slug: collectionParam = null } = {}] = routeMatcher(
        routes.collection,
        location,
    );
    const { collection = null, isFetched: collectionIsFetched = false } = useCollection(
        collectionParam,
        {
            suspense: false,
            enabled: collectionParam !== null,
            // keepPreviousData: false,
            placeholderData: null,
        },
    );

    const defaultBrand = useDefaultBrand();
    const routeBrand = useBrand(brandParam);
    const { metadata: { brands: documentBrands } = {} } = document || {};
    const [documentBrand = null] = documentBrands || [];
    const { metadata: { brands: collectionBrands } = {} } = collection || {};
    const [collectionBrand = null] = collectionBrands || [];
    let finalBrandHandle: Brand | string = defaultBrand;
    let brandLoaded = true;
    if (documentMatch) {
        finalBrandHandle = (documentBrand || {}).handle || null;
        brandLoaded = documentIsFetched;
    } else if (collectionMatch) {
        finalBrandHandle = (collectionBrand || {}).handle || null;
        brandLoaded = collectionIsFetched;
    } else if (brandMatch) {
        finalBrandHandle = (routeBrand || {}).handle || null;
        brandLoaded = routeBrand !== null || brandParam === null;
    }

    const finalBrand = useBrand(finalBrandHandle);

    return {
        brand: finalBrand || defaultBrand,
        loaded: brandLoaded,
    };
}
